/**
 * Tela: brilho e orientação (spec 2026-09-24-robo-painel-de-ajustes, 4.2). A orientação
 * é uma por tela (spec 2026-10-08-robo-orientacao-por-tela): o rosto e as informações
 * podem estar montados em sentidos diferentes na mesma placa. O tema
 * é do dono e só aparece aqui, para quem dá suporte saber o que o visor mostra.
 */
import { useEffect, useState } from 'react'
import { Alert, Button, Group, SegmentedControl, Slider, Stack, Text } from '@mantine/core'

import {
  AVATAR_LABEL, HARDWARE_COM_AVATAR, type AjustesDoRobo, type Avatar, type HardwareModel,
  type MudancaDeAjustes,
} from '@/api/robo'
import { Linha } from './Linha'
import type { Mensagem } from './mensagens'

const BRILHO_PADRAO = 100
const ORIENTACOES = [{ value: '0', label: 'Normal' }, { value: '180', label: 'Virada 180°' }]

/** Qual painel é cada tela, para quem está com o robô na mão (`Config.h`). */
function paineis(modelo: HardwareModel | null): { rosto: string; info: string; versao: string } {
  if (modelo === 'oled_dual') return { rosto: 'o OLED do rosto', info: 'o OLED pequeno', versao: '1.8.0' }
  if (modelo === 'tft_st7735') return { rosto: 'o TFT colorido', info: 'o OLED pequeno', versao: '1.9.0' }
  return { rosto: 'a tela do personagem', info: 'a tela pequena de resumo', versao: '1.9.0 (Robô II) ou 1.8.0 (Robô I)' }
}
const MARCAS = [10, 50, 100].map((v) => ({ value: v, label: `${v}%` }))

export function SecaoTela({
  ajustes, hardwareModel, avatar, podeAjustar, ocupado, situacao, mensagem, aoAplicar,
}: {
  ajustes: AjustesDoRobo
  hardwareModel: HardwareModel | null
  avatar: Avatar | null
  podeAjustar: boolean
  ocupado: boolean
  situacao: Mensagem
  mensagem: Mensagem | null
  aoAplicar: (mudancas: MudancaDeAjustes) => Promise<void>
}) {
  const brilhoSalvo = ajustes.desejado.brilho ?? BRILHO_PADRAO
  const rotacaoSalva = ajustes.desejado.rotacao ?? 0
  // Ausente no backend anterior: vale o que o firmware antigo faz, a do rosto nas duas.
  const rotacaoInfoSalva = ajustes.desejado.rotacao_info ?? 0
  const [brilho, setBrilho] = useState(brilhoSalvo)
  const [rotacao, setRotacao] = useState(rotacaoSalva)
  const [rotacaoInfo, setRotacaoInfo] = useState(rotacaoInfoSalva)
  // O salvo muda por fora (outra aba, a rede, o "Restaurar"): o rascunho acompanha.
  useEffect(() => setBrilho(brilhoSalvo), [brilhoSalvo])
  useEffect(() => setRotacao(rotacaoSalva), [rotacaoSalva])
  useEffect(() => setRotacaoInfo(rotacaoInfoSalva), [rotacaoInfoSalva])

  const mudancas: MudancaDeAjustes = {
    ...(brilho !== brilhoSalvo ? { brilho } : {}),
    ...(rotacao !== rotacaoSalva ? { rotacao } : {}),
    ...(rotacaoInfo !== rotacaoInfoSalva ? { rotacao_info: rotacaoInfo } : {}),
  }
  const tela = paineis(hardwareModel)
  const mudou = Object.keys(mudancas).length > 0
  const travado = !podeAjustar || ocupado

  return (
    <Stack gap="md">
      <Linha rotulo="Brilho">
        <Stack gap={4} w={280}>
          <Slider
            thumbLabel="Brilho"
            min={10} max={100} step={10} marks={MARCAS}
            value={brilho} onChange={setBrilho} disabled={travado}
            label={(v) => `${v}%`}
          />
          <Text size="xs" c="dimmed" mt="sm">
            O mínimo é 10%: com a tela apagada o robô pareceria desligado.
          </Text>
        </Stack>
      </Linha>
      <Linha rotulo="Orientação do rosto">
        <SegmentedControl
          aria-label="Orientação do rosto"
          value={String(rotacao)}
          onChange={(v) => setRotacao(Number(v))}
          disabled={travado}
          data={ORIENTACOES}
        />
        <Text size="xs" c="dimmed">Gira {tela.rosto}.</Text>
      </Linha>
      <Linha rotulo="Orientação das informações">
        <SegmentedControl
          aria-label="Orientação das informações"
          value={String(rotacaoInfo)}
          onChange={(v) => setRotacaoInfo(Number(v))}
          disabled={travado}
          data={ORIENTACOES}
        />
        <Text size="xs" c="dimmed">
          Gira {tela.info}. Separada do rosto a partir do firmware {tela.versao}; antes disso,
          a orientação do rosto vale para as duas telas.
        </Text>
      </Linha>
      {hardwareModel === HARDWARE_COM_AVATAR && (
        <Linha rotulo="Tema">
          <Text size="sm">{avatar ? AVATAR_LABEL[avatar] : 'Inovare (padrão)'}</Text>
          <Text size="xs" c="dimmed">Quem escolhe é o dono, em Meu robô, na seção Aparência.</Text>
        </Linha>
      )}
      <Linha rotulo="Situação">
        <Text size="sm" c={situacao.cor === 'gray' ? 'dimmed' : undefined}>{situacao.texto}</Text>
      </Linha>
      <Group gap="sm">
        <Button onClick={() => aoAplicar(mudancas)} disabled={travado || !mudou} loading={ocupado && mudou}>
          Aplicar na tela
        </Button>
      </Group>
      {mensagem && <Alert color={mensagem.cor} role="status">{mensagem.texto}</Alert>}
    </Stack>
  )
}
